import { IntegrationCard } from './IntegrationCard';
import { DetailKey } from '@overlayqa/shared/ui';
import { IntegrationTile } from './integrationLogos';
import { useState } from 'react';
import { useConnectSlack, useDisconnectSlack, useSlackStatus } from '../../hooks/useIntegrations';
import { integrationsApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { log } from '@/lib/logger';
import type { SlackChannel } from '@overlayqa/shared';

log.info('[SlackIntegrationCard] Module loading...');

/** A failed channels read is not an empty workspace (2026-09-16 sweep, INT-04). */
const CHANNELS_UNAVAILABLE_COPY = "Couldn't load channels.";

export function SlackIntegrationCard() {
  const { status, isLoading, refetch, teamId } = useSlackStatus();
  const connectMutation = useConnectSlack();
  const disconnectMutation = useDisconnectSlack();
  const { getToken } = useAuth();

  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [showChannelPicker, setShowChannelPicker] = useState(false);
  // Channel id whose setSlackChannel POST is in flight. All rows disable
  // while it runs so repeat clicks can't queue conflicting channel writes.
  const [selectingChannelId, setSelectingChannelId] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // `null`: the status read failed (INT-03); the card offers a re-read, not "Connect".
  const isConnected: boolean | null = status ? status.connected : null;
  const isConnecting = connectMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;

  const handleConnect = () => {
    setActionError(null);
    // Funnel step 1 (integration_link_clicked) fires from the dialog's Connect, for every provider; the popup can
    // dead-end on Slack's own pages and never reach /integration-connected, so that click is the attempt's only signal.
    connectMutation.mutate(undefined, {
      onSuccess: () => refetch(),
      onError: (err) => {
        setActionError(err.message || 'Failed to connect Slack. Try again.');
      },
    });
  };

  const handleDisconnect = () => {
    setActionError(null);
    disconnectMutation.mutate(undefined, {
      onSuccess: () => refetch(),
      onError: (err) => {
        setActionError(err.message || 'Failed to disconnect Slack. Try again.');
      },
    });
  };

  const handleLoadChannels = async () => {
    setIsLoadingChannels(true);
    setShowChannelPicker(true);
    setChannelsError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const data = await integrationsApi.getSlackChannels(token, teamId);
      setChannels(data.channels);
    } catch (err) {
      // Until 2026-09-16 this catch set no state, so the empty initial array
      // rendered "No channels found. Invite the OverlayQA bot..." for a read
      // that never happened (INT-04).
      log.error('[SlackIntegrationCard] Failed to load channels', { error: err });
      setChannelsError(CHANNELS_UNAVAILABLE_COPY);
    } finally {
      setIsLoadingChannels(false);
    }
  };

  const handleSelectChannel = async (channel: SlackChannel) => {
    setActionError(null);
    setSelectingChannelId(channel.id);
    try {
      const token = await getToken();
      // A missing session is a failure the catch below reports; a bare return
      // closed the picker's pending state with nothing said (2026-09-16 review).
      if (!token) throw new Error('Not authenticated');
      await integrationsApi.setSlackChannel(token, channel.id, channel.name, teamId);
      setShowChannelPicker(false);
      refetch();
    } catch (err) {
      log.error('[SlackIntegrationCard] Failed to set channel', { error: err });
      setActionError('Failed to set channel. Try again.');
    } finally {
      setSelectingChannelId(null);
    }
  };

  const handleTestMessage = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      await integrationsApi.testSlackConnection(token, teamId);
      setTestResult('success');
    } catch (err) {
      log.error('[SlackIntegrationCard] Test message failed', { error: err });
      setTestResult('error');
    } finally {
      setIsTesting(false);
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  return (
    <IntegrationCard
      provider="slack"
      loading={isLoading}
      tile={<IntegrationTile color="var(--color-slack)"><SlackIcon /></IntegrationTile>}
      name="Slack"
      description="Get a Slack message when an issue is created, assigned, or commented."
      connection={{
        connected: isConnected,
        connecting: isConnecting,
        disconnecting: isDisconnecting,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onRetry: () => void refetch(),
        connectedAt: status?.createdAt,
        rows: [['Workspace', status?.slackTeamName]],
      }}
      errors={[actionError]}
    >
      {/* The Channel row of the dialog's detail card: the chosen channel, then its controls and the picker. */}
      <div className="oq-metadata__row oq-detail-row--stack">
        <DetailKey>Channel</DetailKey>
        <span className="oq-detail-text">{status?.channelName ? `#${status.channelName}` : 'No channel yet'}</span>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <button type="button" onClick={handleLoadChannels} className="btn-secondary btn-sm">
            <span className="text-cap-trim">{status?.channelName ? 'Change channel' : 'Select channel'}</span>
          </button>
          {status?.channelId && (
            <button type="button" onClick={handleTestMessage} disabled={isTesting} className="btn-secondary btn-sm">
              <span className="text-cap-trim">{isTesting ? 'Sending...' : 'Test'}</span>
            </button>
          )}
          {testResult === 'success' && <span className="text-xs text-[var(--color-success)]">Sent</span>}
          {testResult === 'error' && <span className="text-xs text-red-600">Failed to send</span>}
        </div>
        {showChannelPicker && (
          <div className="mt-2 border border-gray-200 rounded-lg bg-white max-h-48 overflow-y-auto">
            {isLoadingChannels ? (
              <p className="p-3 text-sm text-gray-500">Loading channels...</p>
            ) : channelsError ? (
              <div className="p-3 text-sm">
                <p className="text-red-600" role="alert">{channelsError}</p>
                <button type="button" onClick={handleLoadChannels} className="btn-secondary text-xs mt-2">
                  <span className="text-cap-trim">Retry</span>
                </button>
              </div>
            ) : channels.length === 0 ? (
              <p className="p-3 text-sm text-gray-500">
                No channels found. Invite the OverlayQA bot to a channel first.
              </p>
            ) : (
              channels.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => handleSelectChannel(ch)}
                  disabled={selectingChannelId !== null}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ch.isPrivate ? <LockIcon /> : <span className="text-gray-400 mr-1">#</span>}{ch.name}
                  {selectingChannelId === ch.id && (
                    <span className="ml-2 text-xs text-gray-400">Selecting...</span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </IntegrationCard>
  );
}

function SlackIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 127 127" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M27.2 80a13.6 13.6 0 1 1-13.6-13.6h13.6V80zm6.8 0a13.6 13.6 0 1 1 27.2 0v34a13.6 13.6 0 1 1-27.2 0V80z" fill="#E01E5A"/>
      <path d="M47.6 27.2a13.6 13.6 0 1 1 13.6-13.6v13.6H47.6zm0 6.8a13.6 13.6 0 1 1 0 27.2H13.6a13.6 13.6 0 1 1 0-27.2h34z" fill="#36C5F0"/>
      <path d="M99.8 47.6a13.6 13.6 0 1 1 13.6 13.6H99.8V47.6zm-6.8 0a13.6 13.6 0 1 1-27.2 0v-34a13.6 13.6 0 1 1 27.2 0v34z" fill="#2EB67D"/>
      <path d="M79.4 99.8a13.6 13.6 0 1 1-13.6 13.6V99.8h13.6zm0-6.8a13.6 13.6 0 1 1 0-27.2h34a13.6 13.6 0 1 1 0 27.2h-34z" fill="#ECB22E"/>
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="inline-block mr-1 text-gray-400" aria-hidden="true">
      <path fillRule="evenodd" d="M4 6V4a4 4 0 118 0v2h1a1 1 0 011 1v7a1 1 0 01-1 1H3a1 1 0 01-1-1V7a1 1 0 011-1h1zm2-2a2 2 0 114 0v2H6V4z" clipRule="evenodd"/>
    </svg>
  );
}

log.info('[SlackIntegrationCard] Module loaded');
